import {
  formatDateWithGMT,
  formatNumber,
  pluralize,
  tooltipProps
} from '$app/utils'
import Pagination from '$components/Pagination'
import { useRouteSummary } from '$contexts/RouteContext/hooks'
import { useApiListingParams } from '$hooks/actions'
import { useAppUserService } from '$hooks/services'
import { useAppDetailStore } from '$hooks/stores'
import Header from '$layouts/LayoutWithSidebar/Header'
import { redirect, ROUTE_NAMES } from '$router/config'
import { TAppUserListResponse } from '$services/api'
import { flexColumn, flexRow } from '$styles/common.css'
import { Button } from '@genie-fintech/ui/components'
import { status } from '@genie-fintech/ui/style/element'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { footnote, title } from '@genie-fintech/ui/style/typography'
import {
  ExternalLink,
  Plus,
  SquareArrowOutUpRight,
  Table2,
  Users2
} from 'lucide-react'
import { useCallback, useEffect } from 'react'
import { innerMain, main } from './styles.css'
import SearchBox from '$components/SearchBox'
import EmptyData from '$components/EmptyData'
import Table, { Column } from '$components/Table'
import Link from '$router/Link'
import { tableCellLink } from '$components/Table/styles.css'
import { useBreadcrumb } from '$components/Breadcrumb/hooks/useBreadcrumb'
import AppUserCreate from '../Create'
import { useBoolean } from 'ahooks'

type ColumnData = TAppUserListResponse['data'][number]

const { colors } = themeVars

const AppUserList = () => {
  const appName = useAppDetailStore(state => state.appDetail?.name)

  const [
    open,
    { setTrue: setAddNewUserModalOpen, setFalse: setAddNewUserModalClose }
  ] = useBoolean()

  const { route } = useRouteSummary()
  const { appId } = route.params

  const {
    fetchAppUserList,
    fetchingAppUserList,
    users: { list, meta }
  } = useAppUserService()

  const {
    searchValue,
    updateSearchValue,
    debouncedSearchValue,
    pagerProps,
    perPage
  } = useApiListingParams({ total: meta?.total })

  const { currentPage: page } = pagerProps

  useEffect(() => {
    if (!appId) return
    fetchAppUserList({
      page,
      per_page: perPage,
      application_id: appId,
      q: debouncedSearchValue
    })
  }, [appId, fetchAppUserList, page, perPage, debouncedSearchValue])

  useBreadcrumb([{ name: 'Users' }])

  const handleOnClickImport = useCallback(() => {
    redirect(ROUTE_NAMES.APP_USERS_CSV_IMPORT, options => options)
  }, [])

  const isEmpty = !list.length && !fetchingAppUserList

  const columns: Column<ColumnData>[] = [
    {
      name: 'No',
      render: ({ index }) => (page - 1) * perPage + index + 1
    },
    {
      name: 'Username',
      dataIndex: 'name',
      render: ({ record }) => (
        <Link
          to={ROUTE_NAMES.APP_USER_DETAIL}
          options={({ params, ...options }) => ({
            ...options,
            params: { ...params, userId: record.id }
          })}
          className={tableCellLink}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{record.name || 'N/A'}</span>
          <SquareArrowOutUpRight size={16} />
        </Link>
      )
    },
    {
      name: 'Email',
      dataIndex: 'email',
      render: ({ record }) => (
        <span style={{ whiteSpace: 'nowrap' }}>{record.email || 'N/A'}</span>
      )
    },
    {
      name: 'Phone',
      dataIndex: 'phone',
      render: ({ record }) => (
        <span style={{ whiteSpace: 'nowrap' }}>{record.phone || 'N/A'}</span>
      )
    },
    {
      name: 'Last Signed In',
      dataIndex: 'last_login_at',
      render: ({ record }) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {record.last_login_at
            ? formatDateWithGMT(record.last_login_at)
            : 'N/A'}
        </span>
      )
    },
    {
      name: 'Status',
      dataIndex: 'status',
      render: ({ record }) => (
        <span
          className={status({
            kind: record.status === 'active' ? 'success' : 'danger',
            type: 'faint',
            size: 'small'
          })}
          style={{
            fontSize: '1rem', // hack to calculate for status font size
            textTransform: 'uppercase'
          }}
        >
          <span className={footnote.two}>
            {record.status === 'active' ? 'Active' : 'Inactive'}
          </span>
        </span>
      )
    },
    {
      name: 'Role',
      dataIndex: 'role_count',
      render: ({ record }) => formatNumber(record.role_count)
    },
    {
      name: 'Group',
      dataIndex: 'group_count',
      render: ({ record }) => formatNumber(record.group_count)
    }
  ]

  return (
    <>
      <Header style={{ gap: 8 }}>
        <Users2 size={20} />

        <span className={title.two} style={{ paddingLeft: 4 }}>
          Users
        </span>

        <span style={{ flex: 1 }} />

        <Button
          styleVariants={{ kind: 'neutral', type: 'outlined', size: 'small' }}
          onClick={handleOnClickImport}
          disabled
          {...tooltipProps('Coming soon')}
        >
          <Table2 size={14} />
          Import CSV File
        </Button>

        <Button
          styleVariants={{ type: 'outlined', size: 'small' }}
          disabled
          {...tooltipProps('Coming soon')}
        >
          Export
          <ExternalLink size={14} />
        </Button>

        <Button
          onClick={setAddNewUserModalOpen}
          styleVariants={{ size: 'small' }}
        >
          <Plus size={16} />
          Add User
        </Button>
      </Header>

      <main className={main}>
        <article className={innerMain}>
          <header className={flexColumn} style={{ gap: 4 }}>
            <p className={title.two}>{appName}'s USERS</p>
            <p className={footnote.one} style={{ color: colors.neutral[60] }}>
              Manage and review user accounts for efficient system
              administration.
            </p>
          </header>

          <article className={flexColumn} style={{ gap: 16, flex: 1 }}>
            <article className={flexRow} style={{ gap: 4 }}>
              <article style={{ flex: 1, maxWidth: 560 }}>
                <SearchBox
                  value={searchValue}
                  onChange={updateSearchValue}
                  placeholder="Search here..."
                />
              </article>

              <span style={{ flex: 1 }} />

              {!!meta?.total && (
                <span
                  className={title.six}
                  style={{
                    color: colors.text.disabled,
                    textTransform: 'uppercase'
                  }}
                >
                  {pluralize(meta.total, 'user')}
                </span>
              )}
            </article>

            {isEmpty && (
              <EmptyData type="user" onClick={setAddNewUserModalOpen} />
            )}

            {!isEmpty && (
              <Table
                columns={columns}
                data={list}
                loading={fetchingAppUserList}
              />
            )}

            {!!list.length && (
              <Pagination meta={meta} pagerProps={pagerProps} />
            )}
          </article>
        </article>
      </main>

      <AppUserCreate open={open} onClose={setAddNewUserModalClose} />
    </>
  )
}

export default AppUserList
